import { DropdownState } from '@activepieces/pieces-framework';
import {
  Action,
  FlowOperationType,
  isNil,
  Trigger,
  buildDynamicPropertiesKey,
} from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import deepEqual from 'deep-equal';
import { t } from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';

import { MultiSelectPieceProperty } from '../../../components/custom/multi-select-piece-property';

import {
  allRefreshersHaveValue,
  isAuthRefresherMissing,
  useSavedDynamicProperties,
} from './form-utils';

import {
  nextPendingFlowWriteKey,
  useBuilderStateContext,
} from '@/app/builder/builder-hooks';
import { SearchableSelect } from '@/components/custom/searchable-select';
import { piecesApi } from '@/features/pieces/lib/pieces-api';

type SelectPiecePropertyProps = {
  refreshers: string[];
  propertyName: string;
  value?: unknown;
  multiple?: boolean;
  disabled: boolean;
  onChange: (value: unknown | undefined) => void;
  showDeselect?: boolean;
  refreshOnSearch?: boolean;
};
type SelectOption = { label: string; value: React.Key };

const SEARCH_DEBOUNCE_MS = 300;

const DynamicDropdownPieceProperty = React.memo(
  (props: SelectPiecePropertyProps) => {
    const [
      flowVersion,
      readonly,
      applyOperation,
      selectedStep,
      registerPendingFlowWrite,
      resolvePendingFlowWrite,
    ] = useBuilderStateContext((state) => [
      state.flowVersion,
      state.readonly,
      state.applyOperation,
      state.selectedStep,
      state.registerPendingFlowWrite,
      state.resolvePendingFlowWrite,
    ]);
    const form = useFormContext<Action | Trigger>();
    const isFirstRender = useRef(true);
    const previousValues = useRef<undefined | unknown[]>(undefined);

    const { settings } = form.getValues();
    const authRefresher =
      settings.input && 'auth' in settings.input ? ['auth'] : [];
    const newRefreshers = [...props.refreshers, ...authRefresher];
    const [dropdownState, setDropdownState] = useState<DropdownState<unknown>>({
      disabled: false,
      placeholder: 'Select an option',
      options: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const hasCachedData = useRef(false);
    // Only the answer to the last thing typed may land: an earlier search can resolve after it.
    const latestSearchValue = useRef<string | undefined>(undefined);

    useSavedDynamicProperties({
      propertyName: props.propertyName,
      onLoad: (value) => {
        setDropdownState(value as DropdownState<unknown>);
        hasCachedData.current = true;
      },
    });

    const { mutate } = useMutation<
      DropdownState<unknown>,
      Error,
      { input: Record<string, unknown>; searchValue?: string; writeKey: string }
    >({
      // The pending write resolves HERE, not in a mutate() callback: react-query
      // drops those when the component unmounts first, and a step change during
      // a resolution would leave the key pending for the rest of the session.
      mutationFn: async ({ input, searchValue, writeKey }) => {
        try {
          return await resolveOptions(input, searchValue);
        } finally {
          resolvePendingFlowWrite(writeKey);
        }
      },
      onError: () => {
        setIsLoading(false);
      },
      onSuccess: (response, { searchValue }) => {
        if (searchValue !== latestSearchValue.current) {
          return;
        }
        setDropdownState(response);
        setIsLoading(false);
      },
    });

    const resolveOptions = async (
      input: Record<string, unknown>,
      searchValue: string | undefined,
    ): Promise<DropdownState<unknown>> => {
      setIsLoading(true);
      const { settings } = form.getValues();
      const actionOrTriggerName = settings.actionName ?? settings.triggerName;
      const { pieceName, pieceVersion, pieceType, packageType } = settings;

      const result = await piecesApi.options<DropdownState<unknown>>({
        pieceName,
        pieceVersion,
        pieceType,
        packageType,
        propertyName: props.propertyName,
        actionOrTriggerName: actionOrTriggerName,
        input: input,
        flowVersionId: flowVersion.id,
        flowId: flowVersion.flowId,
        searchValue,
      });
      if (searchValue === latestSearchValue.current) {
        const dynamicKey = buildDynamicPropertiesKey({
          flowId: flowVersion.flowId,
          pieceName,
          stepName: String(selectedStep),
          actionOrTriggerName,
          propName: props.propertyName,
        });
        await applyOperation(
          {
            type: FlowOperationType.UPDATE_DYNAMIC_PROPERTIES_VALUES,
            request: { dynamicPropertiesValues: { [dynamicKey]: result } },
          },
          () => console.error('Operation failed'),
        );
      }

      return result;
    };

    /* eslint-disable react-hooks/rules-of-hooks */
    const refresherValues = newRefreshers.map((refresher) =>
      useWatch({
        name: `settings.input.${refresher}` as const,
        control: form.control,
      }),
    );
    /* eslint-enable react-hooks/rules-of-hooks */
    const refresh = (searchValue?: string) => {
      const input: Record<string, unknown> = {};
      newRefreshers.forEach((refresher, index) => {
        input[refresher] = refresherValues[index];
      });
      latestSearchValue.current = searchValue;
      // Resolution is an HTTP round trip, then a flow write: signal it so
      // anything reading the flow server-side waits for it
      const writeKey = nextPendingFlowWriteKey(
        `dynamic-dropdown:${props.propertyName}`,
      );
      registerPendingFlowWrite(writeKey);
      mutate({ input, searchValue, writeKey });
    };
    const search = useDebouncedCallback(refresh, SEARCH_DEBOUNCE_MS);

    useEffect(() => {
      const hasAllNeededValues = allRefreshersHaveValue(
        newRefreshers,
        refresherValues,
      );

      if (hasAllNeededValues) {
        if (
          !isFirstRender.current &&
          !deepEqual(previousValues.current, refresherValues)
        ) {
          props.onChange(null);
          refresh();
        } else if (isFirstRender.current && !hasCachedData.current) {
          refresh();
        }
      } else {
        const isAuthMissing = isAuthRefresherMissing(
          newRefreshers,
          refresherValues,
        );
        setDropdownState({
          disabled: true,
          options: [],
          placeholder: isAuthMissing
            ? 'Please connect your account first'
            : 'Please complete the required fields above',
        });
      }

      previousValues.current = refresherValues;
      isFirstRender.current = false;
    }, refresherValues);

    const selectOptions = useMemo(
      () =>
        (dropdownState.options || []).map((option) => ({
          label: option.label,
          value: option.value as React.Key,
        })),
      [dropdownState.options],
    );

    // A record found by searching is missing from the next list loaded, and would
    // otherwise show as nothing selected.
    const knownSelection = useRef<SelectOption | undefined>(undefined);
    useEffect(() => {
      const selected = selectOptions.find((option) =>
        deepEqual(option.value, props.value),
      );
      if (selected) {
        knownSelection.current = selected;
      }
    }, [selectOptions, props.value]);
    const cachedOptions = knownSelection.current
      ? [knownSelection.current]
      : [];

    const isHexColor = (value: unknown): boolean => {
      if (typeof value !== 'string') return false;
      return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
    };

    const findHexColor = (label: string, value: React.Key): string | null => {
      if (isHexColor(label)) return label;
      if (isHexColor(String(value))) return String(value);
      return null;
    };

    const customValueRenderer = (value: React.Key): React.ReactNode => {
      const option = [...selectOptions, ...cachedOptions].find(
        (opt) => opt.value === value,
      );
      const label = option?.label || String(value);
      const hexColor = findHexColor(label, value);

      if (hexColor) {
        return (
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded border border-gray-300 flex-shrink-0"
              style={{ backgroundColor: hexColor }}
            />
            <span>{label}</span>
          </div>
        );
      }

      return label;
    };

    return props.multiple ? (
      <MultiSelectPieceProperty
        placeholder={t(dropdownState.placeholder ?? 'Select an option')}
        options={selectOptions}
        loading={isLoading}
        onChange={(value) => props.onChange(value)}
        disabled={dropdownState.disabled || props.disabled}
        initialValues={props.value as unknown[]}
        showDeselect={
          props.showDeselect &&
          !isNil(props.value) &&
          Array.isArray(props.value) &&
          props.value.length > 0 &&
          !props.disabled &&
          !dropdownState.disabled
        }
        showRefresh={!isLoading && !readonly}
        onRefresh={() => refresh()}
      />
    ) : (
      <SearchableSelect
        pieceName={form.getValues().settings?.pieceName || ''}
        propertyName={props.propertyName}
        options={selectOptions}
        disabled={dropdownState.disabled || props.disabled}
        loading={isLoading}
        placeholder={t(dropdownState.placeholder ?? 'Select an option')}
        value={props.value as React.Key}
        onChange={(value) => props.onChange(value)}
        showDeselect={
          props.showDeselect && !isNil(props.value) && !props.disabled
        }
        onRefresh={() => refresh()}
        showRefresh={!isLoading && !readonly}
        valuesRendering={customValueRenderer}
        refreshOnSearch={props.refreshOnSearch ? search : undefined}
        cachedOptions={cachedOptions}
      />
    );
  },
);

DynamicDropdownPieceProperty.displayName = 'DynamicDropdownPieceProperty';
export { DynamicDropdownPieceProperty };
